/**
 * Authentication utility functions
 * Handles login state management with localStorage and 1-hour expiration
 * Supports force login mode where authentication is required on every refresh
 */

const AUTH_KEY = 'addiwise_auth';

/**
 * Check if login is required based on environment variable
 * @returns {boolean}
 */
export const isLoginRequired = () => {
  return import.meta.env.VITE_LOGIN_REQUIRED === 'true';
};

/**
 * Check if force login is enabled (requires login on every refresh)
 * @returns {boolean}
 */
export const isForceLoginEnabled = () => {
  return import.meta.env.VITE_FORCE_LOGIN_ENABLED === 'true';
};

/**
 * Validate user credentials against environment variables
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {boolean} - True if credentials are valid
 */
export const validateCredentials = (email, password) => {
  const validEmail = import.meta.env.VITE_LOGIN_EMAIL;
  const validPassword = import.meta.env.VITE_LOGIN_PASSWORD;

  return email === validEmail && password === validPassword;
};

/**
 * Save login state to localStorage
 * If force login is enabled, doesn't persist (auth only in memory)
 */
export const saveLoginState = () => {
  // If force login is enabled, don't persist to localStorage
  // Authentication will only exist in React state (memory)
  if (isForceLoginEnabled()) {
    return;
  }

  localStorage.setItem(AUTH_KEY, 'true');
};

/**
 * Check if user is currently authenticated
 * @returns {boolean} - True if user is logged in
 */
export const isAuthenticated = () => {
  // If force login is enabled, always return false on page load
  // This ensures login is required on every refresh
  if (isForceLoginEnabled()) {
    return false;
  }

  return localStorage.getItem(AUTH_KEY) === 'true';
};

/**
 * Clear login state from localStorage
 */
export const clearLoginState = () => {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(AUTH_EXPIRY_KEY);
};

/**
 * Logout user by clearing login state
 */
export const logout = () => {
  clearLoginState();
};

